import { useCallback, useEffect, useRef, useState } from 'react';

interface StationVolumeSliderProps {
  volume: number;
  onVolumeChange: (volume: number) => void;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  inline?: boolean;
}

export const StationVolumeSlider = ({
  volume,
  onVolumeChange,
  containerRef,
  inline = false,
}: StationVolumeSliderProps) => {
  const [showSlider, setShowSlider] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const isDraggingRef = useRef(false);

  const resetHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    // Don't set timeout if user is actively dragging
    if (!isDraggingRef.current) {
      hideTimeoutRef.current = setTimeout(() => {
        setShowSlider(false);
      }, 6000);
    }
  };

  useEffect(() => {
    if (showSlider) {
      resetHideTimeout();
    }
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [showSlider, volume]);

  const handleIconClick = () => {
    setShowSlider((prev) => {
      if (!prev) {
        resetHideTimeout();
      }
      return !prev;
    });
  };

  const handleSliderInteraction = () => {
    resetHideTimeout();
  };

  const volumeFromClientY = useCallback(
    (clientY: number): number => {
      const track = trackRef.current;
      if (!track) return volume;
      const rect = track.getBoundingClientRect();
      const y = rect.bottom - clientY;
      const pct = y / rect.height;
      return Math.max(0, Math.min(1, pct));
    },
    [volume],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      trackRef.current?.setPointerCapture(e.pointerId);
      onVolumeChange(volumeFromClientY(e.clientY));
    },
    [onVolumeChange, volumeFromClientY],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDraggingRef.current) return;
      onVolumeChange(volumeFromClientY(e.clientY));
    },
    [onVolumeChange, volumeFromClientY],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    trackRef.current?.releasePointerCapture(e.pointerId);
    isDraggingRef.current = false;
    resetHideTimeout();
  }, []);

  const getVolumeIcon = () => {
    if (volume === 0) {
      return (
        <svg
          width="28"
          height="28"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_655_4192)">
            <path
              d="M14.2432 14.6L3.89604 3.21808C3.80327 3.11843 3.67498 3.05936 3.53896 3.05368C3.40294 3.048 3.27016 3.09616 3.16941 3.18772C3.06866 3.27928 3.00806 3.40686 3.00075 3.5428C2.99343 3.67874 3.04 3.81208 3.13034 3.91392L5.58263 6.61131V8.7396C5.58251 9.26661 5.71656 9.78497 5.97216 10.2458C6.22776 10.7067 6.59649 11.0949 7.04361 11.3739C7.49073 11.6529 8.00151 11.8134 8.52782 11.8404C9.05414 11.8674 9.57867 11.7599 10.052 11.5282L10.7698 12.3171C10.138 12.6868 9.41878 12.8806 8.68679 12.8785C7.58946 12.8773 6.53741 12.4408 5.76148 11.6649C4.98555 10.889 4.54911 9.83693 4.54791 8.7396C4.54791 8.60239 4.4934 8.47079 4.39638 8.37377C4.29935 8.27675 4.16776 8.22224 4.03055 8.22224C3.89334 8.22224 3.76174 8.27675 3.66472 8.37377C3.5677 8.47079 3.51319 8.60239 3.51319 8.7396C3.51476 10.0217 3.9917 11.2577 4.85176 12.2085C5.71182 13.1594 6.8939 13.7575 8.16943 13.8873V15.9826C8.16943 16.1199 8.22394 16.2514 8.32096 16.3485C8.41798 16.4455 8.54958 16.5 8.68679 16.5C8.824 16.5 8.95559 16.4455 9.05262 16.3485C9.14964 16.2514 9.20415 16.1199 9.20415 15.9826V13.8867C10.0143 13.8085 10.794 13.5376 11.4779 13.0964L13.4775 15.2952C13.523 15.3464 13.5781 15.3881 13.6398 15.4178C13.7015 15.4476 13.7684 15.4648 13.8368 15.4685C13.9051 15.4722 13.9735 15.4622 14.038 15.4393C14.1025 15.4163 14.1618 15.3808 14.2125 15.3347C14.2632 15.2887 14.3042 15.2331 14.3332 15.171C14.3622 15.109 14.3786 15.0419 14.3815 14.9735C14.3843 14.9051 14.3736 14.8368 14.3499 14.7725C14.3261 14.7083 14.2899 14.6495 14.2432 14.5993V14.6ZM8.68679 10.809C8.13794 10.809 7.61157 10.591 7.22347 10.2029C6.83538 9.81482 6.61735 9.28845 6.61735 8.7396V7.7495L9.3115 10.7127C9.10943 10.7766 8.89873 10.8091 8.68679 10.809ZM12.4066 10.5562C12.6841 9.99093 12.8275 9.36929 12.8257 8.7396C12.8257 8.60239 12.8802 8.47079 12.9772 8.37377C13.0742 8.27675 13.2058 8.22224 13.343 8.22224C13.4802 8.22224 13.6118 8.27675 13.7089 8.37377C13.8059 8.47079 13.8604 8.60239 13.8604 8.7396C13.8625 9.52693 13.6831 10.3041 13.3359 11.0108C13.2934 11.0979 13.2273 11.1713 13.145 11.2226C13.0628 11.274 12.9679 11.3012 12.8709 11.3012C12.7922 11.3013 12.7145 11.2831 12.6439 11.2481C12.5207 11.1878 12.4265 11.0811 12.382 10.9513C12.3375 10.8215 12.3463 10.6794 12.4066 10.5562ZM5.84131 3.36358C6.12624 2.71004 6.62746 2.1745 7.26073 1.84696C7.894 1.51943 8.62071 1.41987 9.31874 1.56501C10.0168 1.71015 10.6435 2.09116 11.0938 2.64398C11.544 3.19681 11.7902 3.88776 11.7909 4.60072V8.7396C11.7908 8.8476 11.7852 8.95552 11.7741 9.06295C11.7608 9.19066 11.7005 9.30885 11.6049 9.39455C11.5092 9.48025 11.3852 9.52733 11.2568 9.52663C11.2387 9.52757 11.2205 9.52757 11.2025 9.52663C11.066 9.51229 10.9409 9.44435 10.8545 9.33775C10.7682 9.23115 10.7277 9.09462 10.742 8.95818C10.7491 8.88705 10.753 8.81332 10.753 8.74089V4.60072C10.7516 4.12605 10.5871 3.66628 10.2871 3.29848C9.98701 2.93069 9.56963 2.67722 9.10491 2.58058C8.64018 2.48393 8.15636 2.54998 7.73454 2.76766C7.31273 2.98534 6.97857 3.34141 6.78808 3.77618C6.7639 3.84253 6.72639 3.90322 6.67786 3.95452C6.62934 4.00583 6.57082 4.04665 6.50592 4.07449C6.44102 4.10232 6.37111 4.11658 6.30049 4.11637C6.22987 4.11617 6.16005 4.10151 6.09531 4.07329C6.03057 4.04508 5.9723 4.00392 5.92407 3.95233C5.87584 3.90075 5.83869 3.83983 5.81489 3.77335C5.7911 3.70686 5.78117 3.6362 5.78571 3.56573C5.79025 3.49526 5.80917 3.42646 5.84131 3.36358Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_655_4192">
              <rect
                width="16"
                height="16"
                fill="white"
                transform="translate(0.5 0.5)"
              />
            </clipPath>
          </defs>
        </svg>
      );
    }
    return (
      <svg
        width="28"
        height="28"
        viewBox="0 0 17 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14.7222 8C14.7222 6.74811 14.3963 5.60558 13.7444 4.5724C13.0926 3.53922 12.2185 2.76621 11.1222 2.25339C10.9 2.14781 10.737 1.98567 10.6333 1.76697C10.5296 1.54827 10.5148 1.32579 10.5889 1.09955C10.6778 0.85822 10.837 0.684766 11.0667 0.579186C11.2963 0.473605 11.5296 0.473605 11.7667 0.579186C13.2037 1.22775 14.3519 2.21946 15.2111 3.5543C16.0704 4.88914 16.5 6.37104 16.5 8C16.5 9.62896 16.0704 11.1109 15.2111 12.4457C14.3519 13.7805 13.2037 14.7722 11.7667 15.4208C11.5296 15.5264 11.2963 15.5264 11.0667 15.4208C10.837 15.3152 10.6778 15.1418 10.5889 14.9005C10.5148 14.6742 10.5296 14.4517 10.6333 14.233C10.737 14.0143 10.9 13.8522 11.1222 13.7466C12.2185 13.2338 13.0926 12.4608 13.7444 11.4276C14.3963 10.3944 14.7222 9.25189 14.7222 8ZM4.05556 10.7376H1.38889C1.13704 10.7376 0.925926 10.6508 0.755556 10.4774C0.585185 10.3039 0.5 10.089 0.5 9.83258V6.21267C0.5 5.95626 0.585185 5.74133 0.755556 5.56787C0.925926 5.39442 1.13704 5.30769 1.38889 5.30769H4.05556L6.98889 2.32127C7.27037 2.03469 7.59259 1.97059 7.95556 2.12896C8.31852 2.28733 8.5 2.57014 8.5 2.97738V13.0679C8.5 13.4751 8.31852 13.7579 7.95556 13.9163C7.59259 14.0747 7.27037 14.0106 6.98889 13.724L4.05556 10.7376ZM12.5 8.02262C12.5 8.65611 12.3593 9.25566 12.0778 9.82127C11.7963 10.3869 11.4259 10.8507 10.9667 11.2127C10.8185 11.3032 10.6667 11.3069 10.5111 11.224C10.3556 11.141 10.2778 11.009 10.2778 10.8281V5.17195C10.2778 4.99095 10.3556 4.85897 10.5111 4.77602C10.6667 4.69306 10.8185 4.69683 10.9667 4.78733C11.4259 5.1644 11.7963 5.63952 12.0778 6.21267C12.3593 6.78582 12.5 7.38914 12.5 8.02262Z"
          fill="currentColor"
        />
      </svg>
    );
  };

  const renderIconButton = () => (
    <button
      ref={iconButtonRef}
      onClick={handleIconClick}
      className={`${
        inline ? 'shrink-0' : 'absolute bottom-6 right-6'
      } z-30 w-14 h-14 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors`}
      style={{
        color: volume === 0 ? '#000' : '#000',
      }}>
      {getVolumeIcon()}
    </button>
  );

  const renderSlider = () => {
    if (!showSlider || !containerRef?.current) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    const parentElement = containerRef.current.offsetParent as HTMLElement;
    const parentRect = parentElement?.getBoundingClientRect();

    const sliderTop = containerRect.top - (parentRect?.top || 0);
    const sliderHeight = containerRect.height;
    const iconButtonRect = iconButtonRef.current?.getBoundingClientRect();
    const sliderLeft = iconButtonRect
      ? iconButtonRect.left + iconButtonRect.width / 2 - (parentRect?.left || 0)
      : containerRect.right - 85;

    return (
      <div
        ref={sliderRef}
        className="absolute z-40 flex items-center justify-center"
        style={{
          left: `${sliderLeft}px`,
          top: `${sliderTop}px`,
          height: `${sliderHeight}px`,
          width: '22px',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
        }}
        onMouseEnter={handleSliderInteraction}
        onMouseMove={handleSliderInteraction}
        onMouseLeave={() => {
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
          }
          hideTimeoutRef.current = setTimeout(() => {
            setShowSlider(false);
          }, 6000);
        }}>
        <div
          ref={trackRef}
          className="relative w-[22px] h-full flex items-center justify-center touch-none select-none"
          style={{
            pointerEvents: 'auto',
            touchAction: 'none',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}>
          <div
            className="absolute w-[22px] rounded-[24px] border-2 border-[rgba(163,163,163,0.30)]"
            style={{
              height: '100%',
              background:
                'linear-gradient(0deg, rgba(0, 0, 1, 0.25) 0%, rgba(0, 0, 1, 0.25) 100%)',
              boxShadow: '0px 0px 6px rgba(190, 54, 157, 0.35) inset',
            }}
          />
          <div
            className="absolute bottom-0"
            style={{
              width: '22px',
              height: `${volume * 100}%`,
              background:
                'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.50) 100%), #FF36CE',
              borderBottomRightRadius: '24px',
              borderBottomLeftRadius: '24px',
            }}
          />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => onVolumeChange(Number(e.target.value))}
            aria-label="Volume"
            className="absolute w-full h-full appearance-none cursor-pointer opacity-0 z-10 pointer-events-none"
            style={{
              writingMode: 'vertical-lr',
              direction: 'rtl',
            }}
          />
          <div
            className="absolute pointer-events-none z-20 w-[46px] h-[46px] left-1/2 bg-white rounded-full flex items-center justify-center shadow-lg"
            style={{
              bottom: `calc(${volume * 100}% - 23px)`,
              transform: 'translateX(-50%)',
              boxShadow:
                '0px 2.767256498336792px 2.2138051986694336px rgba(0, 0, 0, 0.02)',
            }}>
            <div className="w-[18px] h-[18px] bg-[#37163E] rounded-full" />
          </div>
        </div>
      </div>
    );
  };

  if (inline) {
    return (
      <>
        {renderIconButton()}
        {renderSlider()}
      </>
    );
  }

  if (!containerRef?.current) {
    return null;
  }

  return (
    <>
      {renderIconButton()}
      {renderSlider()}
    </>
  );
};
